/**
 * @type Feature (Lint Stabilization)
 * @rationale Replace 'any' with 'unknown' in axiosInterceptor.ts to satisfy Pass 3 of the stabilization plan.
 */
import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import api from './axios';
import { errorForwardingService } from '../utils/errorForwarding';
import { sanitizeData, sanitizeUrl, sanitizeParams } from '../utils/sanitize';

// Request metadata is stamped onto the axios config in the request interceptor and
// read back from error.config in the response handler (same request-scoped object).
// This replaces the old Date.now()-keyed map, whose key was recomputed at response
// time and therefore never matched.
interface RequestMeta {
  method?: string;
  url?: string;
  data?: unknown;
  params?: unknown;
}
type ConfigWithMeta = InternalAxiosRequestConfig & { _requestMeta?: RequestMeta };

// Most recent server-generated request id seen on any response (success or
// error). Requires the backend to expose it via Access-Control-Expose-Headers.
let lastRequestId: string | undefined;

/** The request id from the most recent response, for support correlation. */
export function getLastRequestId(): string | undefined {
  return lastRequestId;
}

function readRequestId(headers: unknown): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  const record = headers as Record<string, string>;
  return record['x-request-id'] ?? record['X-Request-ID'];
}

/**
 * Custom event fired on a 401 for a non-auth endpoint. The app shell (AuthContext)
 * can listen and re-authenticate/redirect. We deliberately do NOT navigate from
 * this global interceptor: a hard redirect here risks loops with the login/`/auth`
 * flow. Auth errors are handled distinctly and never open the copy-details modal.
 */
export const UNAUTHORIZED_EVENT = 'app:unauthorized';

/**
 * Capture the request id from an error response and, on a 401 for a non-auth
 * endpoint, signal the app shell to re-authenticate. Exported so it can be unit
 * tested without a live server. Returns the captured request id, if any.
 */
export function handleAxiosErrorSignals(error: AxiosError): string | undefined {
  const requestId = readRequestId(error.response?.headers);
  if (requestId) {
    lastRequestId = requestId;
    (error as { requestId?: string }).requestId = requestId;
  }

  const requestUrl = String(error.config?.url ?? '');
  if (
    error.response?.status === 401 &&
    !requestUrl.includes('/auth/') &&
    typeof window !== 'undefined'
  ) {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { requestId } }));
  }

  return requestId;
}

export function setupAxiosInterceptors() {
  // Request interceptor - store request data
  api.interceptors.request.use(
    (config) => {
      // Stamp request metadata onto the config so the response error handler can
      // read it back from error.config — no shared map, no timestamp key.
      (config as ConfigWithMeta)._requestMeta = {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
        params: config.params,
      };
      return config;
    },
    (error) => {
      // Request setup error (rare)
      errorForwardingService.reportNetworkError({
        message: `Request setup failed: ${error.message}`,
        httpMethod: error.config?.method?.toUpperCase(),
        requestUrl: error.config?.url,
        errorCode: 'REQUEST_SETUP_ERROR',
        stack: error.stack,
      });
      return Promise.reject(error);
    }
  );

  // Response interceptor - catch all errors
  api.interceptors.response.use(
    (response) => {
      // Capture the browser-visible request id from every success response so
      // support can correlate any screenshot to a backend log line.
      const rid = readRequestId(response.headers);
      if (rid) lastRequestId = rid;
      return response;
    },
    (error: AxiosError) => {
      const config = error.config;

      // Capture request id (surfaced to the shared parser) + signal 401s.
      handleAxiosErrorSignals(error);

      // Extract request details
      const method = config?.method?.toUpperCase() || 'UNKNOWN';
      const url = config?.url || 'unknown';
      const fullUrl = config?.baseURL ? `${config.baseURL}${url}` : url;
      // Redact query-string secrets before the URL is baked into the message
      // (and the requestUrl field), e.g. `?access_token=…` on a failing GET.
      const safeUrl = sanitizeUrl(fullUrl) ?? fullUrl;

      // Request metadata rides along on error.config (stamped in the request
      // interceptor), so it always matches — unlike the old timestamp key.
      const requestMeta = (config as ConfigWithMeta | undefined)?._requestMeta;

      // Determine error type and message
      let errorMessage = error.message;
      let errorCode = error.code || 'UNKNOWN_ERROR';
      const statusCode = error.response?.status;
      const responseData = error.response?.data;

      // Categorize error types
      if (error.code === 'ERR_NETWORK') {
        errorMessage = `Network Error: Unable to reach ${safeUrl}. Could be CORS, server down, or connection issue.`;
        errorCode = 'ERR_NETWORK';
      } else if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
        errorMessage = `Request Canceled: ${method} ${safeUrl}`;
        errorCode = error.code;
      } else if (error.code === 'ERR_BAD_REQUEST') {
        errorMessage = `Bad Request (${statusCode}): ${method} ${safeUrl}`;
        errorCode = 'ERR_BAD_REQUEST';
      } else if (error.code === 'ERR_BAD_RESPONSE') {
        errorMessage = `Bad Response (${statusCode}): ${method} ${safeUrl}`;
        errorCode = 'ERR_BAD_RESPONSE';
      } else if (statusCode) {
        // HTTP error with status code
        const statusText = error.response?.statusText || 'Error';
        errorMessage = `HTTP ${statusCode} ${statusText}: ${method} ${safeUrl}`;

        // Add response message if available
        if (responseData && typeof responseData === 'object') {
            const data = responseData as Record<string, unknown>;
            const detail = data.detail || data.message || data.error;
            if (typeof detail === 'string') {
              errorMessage += ` - ${detail}`;
            }
        }
      }

      // Defense-in-depth: never forward auth request bodies (they carry id_token /
      // passwords, no telemetry value). Otherwise forward the deep-redacted body
      // and — for GET/query errors — the query params, which are the real payload.
      const isAuthRequest = String(config?.url ?? '').includes('/auth/');
      const requestBody = requestMeta?.data ?? config?.data;
      const requestParams = requestMeta?.params ?? config?.params;
      // URLSearchParams keeps its entries off the object's own keys, so
      // Object.keys() alone would report it as empty and drop it silently.
      const isSearchParams =
        typeof URLSearchParams !== 'undefined' && requestParams instanceof URLSearchParams;
      const hasParams =
        !!requestParams &&
        typeof requestParams === 'object' &&
        (isSearchParams
          ? !(requestParams as URLSearchParams).entries().next().done
          : Object.keys(requestParams as object).length > 0);
      let forwardedRequestData: unknown;
      if (isAuthRequest) {
        forwardedRequestData = undefined;
      } else if (hasParams) {
        // Body keeps key-based redaction so benign fields stay readable; query
        // params get VALUE-based redaction because a secret can hide under a
        // benign-looking key (?code=, ?sig=) the key pattern won't catch — same
        // distrust sanitizeUrl applies to the URL query string.
        const safeParams = sanitizeParams(requestParams);
        forwardedRequestData =
          requestBody !== undefined
            ? { body: sanitizeData(requestBody), params: safeParams }
            : { params: safeParams };
      } else {
        forwardedRequestData = sanitizeData(requestBody);
      }

      // Report to error service
      errorForwardingService.reportNetworkError({
        message: errorMessage,
        httpMethod: method,
        requestUrl: safeUrl,
        statusCode,
        requestData: forwardedRequestData,
        responseData: sanitizeData(responseData),
        errorCode,
        stack: error.stack,
        requestId: (error as { requestId?: string }).requestId,
      });

      // Re-throw error so app can handle it normally
      return Promise.reject(error);
    }
  );
}

// Also setup interceptor for global axios (in case someone uses it directly)
export function setupGlobalAxiosInterceptors() {
  axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const config = error.config;
      const method = config?.method?.toUpperCase() || 'UNKNOWN';
      const url = config?.url || 'unknown';
      const safeUrl = sanitizeUrl(url) ?? url;

      errorForwardingService.reportNetworkError({
        message: `Global Axios Error: ${error.message} (${method} ${safeUrl})`,
        httpMethod: method,
        requestUrl: safeUrl,
        statusCode: error.response?.status,
        errorCode: error.code || 'UNKNOWN',
        stack: error.stack,
      });

      return Promise.reject(error);
    }
  );
}
